import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import ProtectedRoute from './components/common/ProtectedRoute';
import ConsentGate from './components/common/ConsentGate';
import ParticipationGate from './components/common/ParticipationGate';
import { FEATURE_FLAGS } from './config/featureFlags';
import { LEARN_ROUTES, LEARN_ROUTE_PATTERNS, LEGACY_REDIRECTS } from './config/learnRoutes';
import Layout from './components/layout/Layout';

// Eager-loaded (needed immediately on first paint)
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import JoinRedirect from './pages/JoinRedirect';
import FcUatPage from './pages/FcUatPage';
import KeyholeEntry from './pages/KeyholeEntry';
import RootRedirect from './components/RootRedirect';
import FlarePendingFlush from './components/FlarePendingFlush';

// Lazy-loaded pages (split into separate JS chunks for faster initial load)
// Legacy 20-question MCQ test (the `tests` router: TestStartPage / TestSessionPage /
// TestResultPage) is UNROUTED as of the KAN-273 flow rework — the FC assessment now
// runs on /ccs/fc-attempt (FcAssessmentPage). Files retained; admin question
// management still uses the router.
const FcAssessmentPage     = lazy(() => import('./pages/FcAssessmentPage'));
const NewWordPrizePage     = lazy(() => import('./pages/NewWordPrizePage'));
const NewWordListPage      = lazy(() => import('./pages/NewWordListPage'));
const NewWordSubmitPage    = lazy(() => import('./pages/NewWordSubmitPage'));
const NewWordDetailPage    = lazy(() => import('./pages/NewWordDetailPage'));
const ThoughtChallengePage = lazy(() => import('./pages/ThoughtChallengePage'));
const ChallengeListPage    = lazy(() => import('./pages/ChallengeListPage'));
const ChallengeDetailPage  = lazy(() => import('./pages/ChallengeDetailPage'));
const ChallengeSubmitPage  = lazy(() => import('./pages/ChallengeSubmitPage'));
const LearningTheoryPage   = lazy(() => import('./pages/LearningTheoryPage'));
const TheoryListPage       = lazy(() => import('./pages/TheoryListPage'));
const TheorySubmitPage     = lazy(() => import('./pages/TheorySubmitPage'));
const TheoryDetailPage     = lazy(() => import('./pages/TheoryDetailPage'));
const LeaderboardPage      = lazy(() => import('./pages/LeaderboardPage'));
const UserDashboardPage    = lazy(() => import('./pages/UserDashboardPage'));
const EditProfilePage      = lazy(() => import('./pages/EditProfilePage'));
const MyInvitesPage        = lazy(() => import('./pages/MyInvitesPage'));
const MyReputationPage     = lazy(() => import('./pages/MyReputationPage'));
const MySJPPage            = lazy(() => import('./pages/MySJPPage'));
const PublicUserPage       = lazy(() => import('./pages/PublicUserPage'));
const AdminDashboardPage   = lazy(() => import('./pages/AdminDashboardPage'));
const AdminUsersPage       = lazy(() => import('./pages/AdminUsersPage'));
const AdminRolesPage           = lazy(() => import('./pages/AdminRolesPage'));
const AdminTestRunsPage        = lazy(() => import('./pages/AdminTestRunsPage'));
const AdminTestersPage         = lazy(() => import('./pages/AdminTestersPage'));
const AdminOpportunitiesPage    = lazy(() => import('./pages/AdminOpportunitiesPage'));
const AdminCompetitionPage      = lazy(() => import('./pages/AdminCompetitionPage'));
const AdminContentHubPage      = lazy(() => import('./pages/AdminContentHubPage'));
const AdminBoutBankPage        = lazy(() => import('./pages/AdminBoutBankPage'));
const AdminBoutItemPage        = lazy(() => import('./pages/AdminBoutItemPage'));
const AdminChallengeListPage   = lazy(() => import('./pages/AdminChallengeListPage'));
const AdminCurriculumCoveragePage = lazy(() => import('./pages/AdminCurriculumCoveragePage'));
const AdminQuestionsPage   = lazy(() => import('./pages/AdminQuestionsPage'));
const AdminNewWordsPage    = lazy(() => import('./pages/AdminNewWordsPage'));
const AdminFcBankPage      = lazy(() => import('./pages/AdminFcBankPage'));
const AdminFcItemPage      = lazy(() => import('./pages/AdminFcItemPage'));
const MindCompilerPage         = lazy(() => import('./pages/MindCompilerPage'));
const JudgmentPipelinePage     = lazy(() => import('./pages/JudgmentPipelinePage'));
const SecuritySettingsPage     = lazy(() => import('./pages/SecuritySettingsPage'));
const ConfirmEmailChangePage   = lazy(() => import('./pages/ConfirmEmailChangePage'));
const GuardianApprovePage      = lazy(() => import('./pages/GuardianApprovePage'));
const ForgotPasswordPage       = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage        = lazy(() => import('./pages/ResetPasswordPage'));
const VerifyEmailPage          = lazy(() => import('./pages/VerifyEmailPage'));
const CcsBoutPage              = lazy(() => import('./pages/CcsBoutPage'));
const BoundaryLandingPage      = lazy(() => import('./pages/BoundaryLandingPage'));
const LearningProjectsPage     = lazy(() => import('./pages/LearningProjectsPage'));
const CcsOnboardingPage        = lazy(() => import('./pages/CcsOnboardingPage'));
const CcsDiagnosticPage        = lazy(() => import('./pages/CcsDiagnosticPage'));
const CcsConstellationPage     = lazy(() => import('./pages/CcsConstellationPage'));
const CcsProfilePage           = lazy(() => import('./pages/CcsProfilePage'));
const AdminCcsTelemetryPage    = lazy(() => import('./pages/AdminCcsTelemetryPage'));
const CcsRecheckPage           = lazy(() => import('./pages/CcsRecheckPage'));
const FlareSessionPage         = lazy(() => import('./pages/FlareSessionPage'));
const FlareReadoutPage         = lazy(() => import('./pages/FlareReadoutPage'));
const LearnDashboardPage       = lazy(() => import('./pages/LearnDashboardPage'));
const CcsInterviewPage         = lazy(() => import('./pages/CcsInterviewPage'));
const AnswerTesterPage         = lazy(() => import('./pages/AnswerTester'));
const CapabilityAgentPage      = lazy(() => import('./pages/CapabilityAgentPage'));
const AboutYouPage             = lazy(() => import('./pages/AboutYouPage'));
const NotificationsPage        = lazy(() => import('./pages/NotificationsPage'));
const ProvingGroundsPage       = lazy(() => import('./pages/ProvingGroundsPage'));
const StudyPlanPage            = lazy(() => import('./pages/StudyPlanPage'));
const TestFeedbackPage         = lazy(() => import('./pages/TestFeedbackPage'));
const OpportunityPage          = lazy(() => import('./pages/OpportunityPage'));
const CompetitionPage          = lazy(() => import('./pages/CompetitionPage'));       // COMP
const CompetitionEntryPage     = lazy(() => import('./pages/CompetitionEntryPage'));  // COMP
const DetailedProfilePage      = lazy(() => import('./pages/DetailedProfilePage'));

// Minimal fallback shown during lazy page load
const PageLoader = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
    <div className="spinner spinner-lg" aria-label="Loading page..." />
  </div>
);

// All pages include their own <Layout> wrapper, so routes render them directly.
//
// EXCEPTION (2026-09-02) — the persistent CCS pages (/learn, /learn/map, /learn/profile) do NOT
// include Layout, and had no outbound link of any kind: no header, no nav, no logo, nothing. A
// learner who reached the learn section could only leave with the browser back button. They are
// wrapped HERE rather than inside each page because they have 20 return paths between them
// (loading, error, empty, and several content states) and wrapping per-return would silently miss
// some.
//
// Deliberately NOT wrapped: /learn/start (onboarding + interview) and /learn/diagnostic (the FC
// assessment). Those are single-task flows on a frozen draw — offering a nav bar mid-assessment
// invites people to wander out of a measurement that cannot be resumed cleanly.

const NotFoundPage = () => (
  <div className="container mt-lg">
    <h1>404 - Page Not Found</h1>
  </div>
);

// /test/session/:id and /test/result/:id (legacy MCQ test) resolve into the new
// assessment path: an in-flight legacy session restarts on the fc-attempt path;
// an emailed result link lands on the map, which is where FC results now live.
const LegacyFcSessionRedirect = () => <Navigate to={LEARN_ROUTES.fc} replace />;
const LegacyFcResultRedirect = () => <Navigate to={LEARN_ROUTES.map} replace />;

const AppRoutes = () => {
  return (
    <Suspense fallback={<PageLoader />}>
    {/* Renders nothing. Posts a first challenge completed before registration, once an
        account exists — via register OR sign-in, which is why it is here and not in
        RegisterPage. See components/FlarePendingFlush.js. */}
    <FlarePendingFlush />
    <Routes>
      {/* Root funnels into the NATIVE flow: a new account goes to onboarding + the AI interview
          (/learn/start), which hands off to the native FC diagnostic and the constellation; a
          returning signed-in user lands on /home. The Keyhole pilot stays mounted at /enter for
          direct invite links, but is no longer the default entry (Mr. Chen, ~22-23 Aug). */}
      <Route path="/" element={<RootRedirect />} />
      <Route path="/home" element={<HomePage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/join" element={<JoinRedirect />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />

      {/* Seed-user pilot entry (2026-08-20): auth + age/participation gate, then hand off
          to Paul's static Keyhole experience same-origin so its FC runs authenticated
          (no local-fallback banner, placements captured). Invite link: /login?next=/enter
          (or /register?next=/enter for new accounts). */}
      <Route
        path="/enter"
        element={
          <ProtectedRoute>
            <ConsentGate>
              <ParticipationGate>
                <KeyholeEntry />
              </ParticipationGate>
            </ConsentGate>
          </ProtectedRoute>
        }
      />
      {/* KAN-455: /welcome retired 2 Oct. It was the April onboarding page; all three of its
          buttons led to features retired or hidden since July. Old links land on Learn. */}
      <Route path="/welcome" element={<Navigate to={LEARN_ROUTES.dashboard} replace />} />

      {/* FC assessment — /learn/assessment/futurecapability (KAN-273 reworked flow,
          step 6), on the platform's fc-attempt path (server-frozen draw, key-stripped
          items, server-side scoring → dimension-grain placements). Captures per-item
          timing, so it sits behind the interaction-monitoring consent gate like the
          legacy test did; the fc-attempt endpoints require participation. */}
      {FEATURE_FLAGS.CCS && (
        <Route
          path={LEARN_ROUTES.fc}
          element={
            <ProtectedRoute>
              <ConsentGate>
                <ParticipationGate>
                  <FcAssessmentPage />
                </ParticipationGate>
              </ConsentGate>
            </ProtectedRoute>
          }
        />
      )}
      <Route path={LEARN_ROUTE_PATTERNS.fcSession} element={<LegacyFcSessionRedirect />} />
      <Route path={LEARN_ROUTE_PATTERNS.fcResult} element={<LegacyFcResultRedirect />} />
      <Route path="/test/session/:sessionId" element={<LegacyFcSessionRedirect />} />
      <Route path="/test/result/:sessionId" element={<LegacyFcResultRedirect />} />
      {LEGACY_REDIRECTS.map((r) => (
        <Route key={r.from} path={r.from} element={<Navigate to={r.to} replace />} />
      ))}

      {/* New Word Prize Routes — hidden from MVP via FEATURE_FLAGS.NEW_WORDS
          (KAN-130). Components/backend preserved; routes redirect home when off. */}
      {FEATURE_FLAGS.NEW_WORDS && <Route path="/new-word-prize" element={<NewWordPrizePage />} />}
      {FEATURE_FLAGS.NEW_WORDS && <Route path="/new-words" element={<NewWordListPage />} />}
      {FEATURE_FLAGS.NEW_WORDS && (
        <Route
          path="/new-words/submit"
          element={
            <ProtectedRoute>
              <NewWordSubmitPage />
            </ProtectedRoute>
          }
        />
      )}
      {FEATURE_FLAGS.NEW_WORDS && <Route path="/new-words/:id" element={<NewWordDetailPage />} />}
      {!FEATURE_FLAGS.NEW_WORDS && <Route path="/new-word-prize" element={<Navigate to="/" replace />} />}
      {!FEATURE_FLAGS.NEW_WORDS && <Route path="/new-words/*" element={<Navigate to="/" replace />} />}

      {/* Thought Challenge Routes — hidden from MVP via FEATURE_FLAGS.CHALLENGE_SUBMISSION
          (KAN-130). Components/backend preserved; routes redirect home when off. */}
      {FEATURE_FLAGS.CHALLENGE_SUBMISSION && <Route path="/thought-challenge" element={<ThoughtChallengePage />} />}
      {FEATURE_FLAGS.CHALLENGE_SUBMISSION && <Route path="/challenges" element={<ChallengeListPage />} />}
      {FEATURE_FLAGS.CHALLENGE_SUBMISSION && <Route path="/challenges/:id" element={<ChallengeDetailPage />} />}
      {FEATURE_FLAGS.CHALLENGE_SUBMISSION && (
        <Route
          path="/challenges/:id/submit"
          element={
            <ProtectedRoute>
              <ChallengeSubmitPage />
            </ProtectedRoute>
          }
        />
      )}
      {!FEATURE_FLAGS.CHALLENGE_SUBMISSION && <Route path="/thought-challenge" element={<Navigate to="/" replace />} />}
      {!FEATURE_FLAGS.CHALLENGE_SUBMISSION && <Route path="/challenges/*" element={<Navigate to="/" replace />} />}

      {/* Learning Theory Routes — hidden from MVP via FEATURE_FLAGS.LEARNING_THEORY
          (KAN-209). Components/backend preserved; routes redirect home when off. */}
      {FEATURE_FLAGS.LEARNING_THEORY && <Route path="/learning-theory" element={<LearningTheoryPage />} />}
      {FEATURE_FLAGS.LEARNING_THEORY && <Route path="/learning-theories" element={<TheoryListPage />} />}
      {FEATURE_FLAGS.LEARNING_THEORY && (
        <Route
          path="/learning-theories/submit"
          element={
            <ProtectedRoute>
              <TheorySubmitPage />
            </ProtectedRoute>
          }
        />
      )}
      {FEATURE_FLAGS.LEARNING_THEORY && <Route path="/learning-theories/:id" element={<TheoryDetailPage />} />}
      {!FEATURE_FLAGS.LEARNING_THEORY && <Route path="/learning-theory" element={<Navigate to="/" replace />} />}
      {!FEATURE_FLAGS.LEARNING_THEORY && <Route path="/learning-theories/*" element={<Navigate to="/" replace />} />}

      {/* Leaderboards */}
      <Route path="/leaderboards" element={<LeaderboardPage />} />

      {/* ── /learn — the reworked learner flow (KAN-273 proposal §1) ──────────────
          /learn                    dashboard (to-dos gate on state)
          /learn/assessment/<flare> first experience     /learn/analysis   its readout
          /learn/map                constellation         /learn/profile    Tell us more
          /learn/interview          Talk to us            /learn/build      the Boundary
          Paths come from config/learnRoutes.js; nothing here is hard-coded. */}
      {FEATURE_FLAGS.CCS && (
        <Route
          path={LEARN_ROUTES.dashboard}
          element={
            <ProtectedRoute>
              {/* KAN-259: sign-up lands here, so an under-18 meets the guardian step on
                  arrival. `arrival` never blocks an adult or a failed status read. */}
              <ParticipationGate arrival>
                <LearnDashboardPage />
              </ParticipationGate>
            </ProtectedRoute>
          }
        />
      )}
      {/* "Tell us more" — the (to-be-trimmed) profile build, formerly /learn/start */}
      {/* KAN-259/KAN-488: the profile and the interview store a learner's self-ratings and
          answers, so they sit behind the participation gate like the loop routes — an under-18
          meets the guardian step here, and the server refuses the writes (403) regardless. */}
      {FEATURE_FLAGS.CCS && (
        <Route
          path={LEARN_ROUTES.profile}
          element={
            <ProtectedRoute>
              <ConsentGate>
                <ParticipationGate>
                  <CcsOnboardingPage />
                </ParticipationGate>
              </ConsentGate>
            </ProtectedRoute>
          }
        />
      )}
      {/* "Talk to us" — the AI interview, standalone */}
      {FEATURE_FLAGS.CCS && (
        <Route
          path={LEARN_ROUTES.interview}
          element={
            <ProtectedRoute>
              <ConsentGate>
                <ParticipationGate>
                  <CcsInterviewPage />
                </ParticipationGate>
              </ConsentGate>
            </ProtectedRoute>
          }
        />
      )}
      {/* Readout of the first experience (session debrief only) */}
      {FEATURE_FLAGS.CCS && (
        <Route
          path={LEARN_ROUTES.analysis}
          element={
            <ProtectedRoute>
              <FlareReadoutPage />
            </ProtectedRoute>
          }
        />
      )}
      {FEATURE_FLAGS.CCS && (
        <Route
          path={LEARN_ROUTES.map}
          element={
            <ProtectedRoute>
              <Layout>
                <CcsConstellationPage />
              </Layout>
            </ProtectedRoute>
          }
        />
      )}
      {/* Native in-app FC diagnostic (KAN-243 follow-on) — off until the native path
          is chosen post-UAT; the diagnostic is the prototype for mid-Aug. */}
      {FEATURE_FLAGS.CCS && FEATURE_FLAGS.CCS_NATIVE_DIAGNOSTIC && (
        <Route
          path="/learn/diagnostic"
          element={
            <ProtectedRoute>
              <CcsDiagnosticPage />
            </ProtectedRoute>
          }
        />
      )}
      {/* KAN-281 evidence-tier profile (was /learn/profile before the flow rework) */}
      {/* V1.4 Mind Compiler MC03/MC07 — what we understand about how the learner plans and decides. */}
      {FEATURE_FLAGS.CCS && (
        <Route
          path={LEARN_ROUTES.aboutYou}
          element={
            <ProtectedRoute>
              <Layout>
                <AboutYouPage />
              </Layout>
            </ProtectedRoute>
          }
        />
      )}
      {/* Day 7 — the Detailed Profile (personal introduction and questionnaire). Linked from the
          dashboard when the server says it is due; the server decides everything else. */}
      {FEATURE_FLAGS.CCS && (
        <Route
          path={LEARN_ROUTES.detailedProfile}
          element={
            <ProtectedRoute>
              <ConsentGate>
                <ParticipationGate>
                  <Layout>
                    <DetailedProfilePage />
                  </Layout>
                </ParticipationGate>
              </ConsentGate>
            </ProtectedRoute>
          }
        />
      )}
      {/* KAN-259 — the user test's end-of-test feedback form. Linked from the dashboard notice
          while it is open; the server decides whether it is open for this tester. */}
      {FEATURE_FLAGS.CCS && (
        <Route
          path={LEARN_ROUTES.testFeedback}
          element={
            <ProtectedRoute>
              <Layout>
                <TestFeedbackPage />
              </Layout>
            </ProtectedRoute>
          }
        />
      )}
      {/* KAN-259 (13770) — the learner's Opportunity experience. Linked from the dashboard notice
          only when the server's OPPORTUNITY_LEARNER_ENABLED is on; renders nothing while it is off. */}
      {FEATURE_FLAGS.CCS && (
        <Route
          path={LEARN_ROUTES.opportunity}
          element={
            <ProtectedRoute>
              <Layout>
                <OpportunityPage />
              </Layout>
            </ProtectedRoute>
          }
        />
      )}
      {/* KAN-535 — the daily study plan (KAN-330). Linked from the /learn dashboard. */}
      {FEATURE_FLAGS.CCS && (
        <Route
          path={LEARN_ROUTES.studyPlan}
          element={
            <ProtectedRoute>
              <Layout>
                <StudyPlanPage />
              </Layout>
            </ProtectedRoute>
          }
        />
      )}
      {FEATURE_FLAGS.CCS && (
        <Route
          path={LEARN_ROUTES.evidence}
          element={
            <ProtectedRoute>
              <Layout>
                <CcsProfilePage />
              </Layout>
            </ProtectedRoute>
          }
        />
      )}
      {/* KAN-534 — the Proving Grounds session page. The reminder email links here
          (session_url in ccs_proving_grounds_service.send_due_reminders). Not behind a flag:
          an emailed link must always resolve. Replaces the KAN-230 redirect stopgap. */}
      <Route
        path="/learn/proving-grounds/:sessionId"
        element={
          <ProtectedRoute>
            <Layout>
              <ProvingGroundsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* KAN-336 — answer-chain tester surface. Any authenticated user (stand-up 2026-08-25);
          the page self-handles a 404 as "chain not enabled yet", so it's safe to route
          regardless of the server-side answer_chain_enabled gate. */}
      <Route
        path="/learn/answer-tester"
        element={
          <ProtectedRoute>
            <AnswerTesterPage />
          </ProtectedRoute>
        }
      />
      {/* Chen's fourth launch module. POST /api/agent/capability/chat runs the memory loop
          (load_memory → context → store_memory) and has done since KAN-182, but nothing in
          the product called it, so it had never run for a real user. This route is the
          caller. Deliberately NOT behind FEATURE_FLAGS.CCS: the loop is independent of the
          bout runner, and gating it behind an unrelated flag is how it stayed unreachable.
          The page self-handles a 404 as "not switched on", so routing it is safe.
          KAN-259/KAN-488: the chat stores the learner's words in memory, so the server refuses
          it (403) until a guardian approves an under-18; the route meets them with the guardian
          step, like the other learner pages. */}
      <Route
        path="/learn/agent"
        element={
          <ProtectedRoute>
            <ParticipationGate>
              <CapabilityAgentPage />
            </ParticipationGate>
          </ProtectedRoute>
        }
      />
      {FEATURE_FLAGS.CCS && (
        <Route
          path="/learn/recheck"
          element={
            <ProtectedRoute>
              <ConsentGate>
                <ParticipationGate>
                  <CcsRecheckPage />
                </ParticipationGate>
              </ConsentGate>
            </ProtectedRoute>
          }
        />
      )}
      {/* "Build future capability" — the Boundary (KAN-147/273). /learn/build is
          the course-shell landing (growth-edge set, one open step — conflict H);
          the bout runner itself lives one level down at /learn/build/bout. */}
      {FEATURE_FLAGS.CCS && (
        <Route
          path={LEARN_ROUTES.build}
          element={
            <ProtectedRoute>
              <ConsentGate>
                <ParticipationGate>
                  <BoundaryLandingPage />
                </ParticipationGate>
              </ConsentGate>
            </ProtectedRoute>
          }
        />
      )}
      {FEATURE_FLAGS.CCS && (
        <Route
          path={LEARN_ROUTES.boutRunner}
          element={
            <ProtectedRoute>
              <ConsentGate>
                <ParticipationGate>
                  <Layout><CcsBoutPage /></Layout>
                </ParticipationGate>
              </ConsentGate>
            </ProtectedRoute>
          }
        />
      )}
      {/* KAN-538: the learning loop's Project step, linked from the Boundary. */}
      {FEATURE_FLAGS.CCS && (
        <Route
          path={LEARN_ROUTES.project}
          element={
            <ProtectedRoute>
              <ConsentGate>
                <ParticipationGate>
                  <Layout><LearningProjectsPage /></Layout>
                </ParticipationGate>
              </ConsentGate>
            </ProtectedRoute>
          }
        />
      )}
      {/* Flare short set (KAN-273 reworked flow, step 1) - behind FLARE_SHORT_SET.
          Session debrief only; the constellation stays dark until FC.

          DELIBERATELY NOT behind ProtectedRoute, as of 2026-09-17.

          Mr. Chen's item-4 decision: "Experience the first opening challenge →
          explain the next five days and invite registration after that challenge …
          Do not place registration and all questionnaires before the first experience."

          The wrapper was the whole of what stood in the way. The backend has accepted
          anonymous callers here since KAN-278 (`get_current_user_optional`, a nullable
          `user_id`, and a `/link` endpoint for attaching the session to an account
          created afterwards) — built, migrated and covered by passing tests, and
          unreachable from the product because this route required a session. */}
      {FEATURE_FLAGS.CCS && FEATURE_FLAGS.FLARE_SHORT_SET && (
        <Route path={LEARN_ROUTES.flare} element={<FlareSessionPage />} />
      )}
      {!FEATURE_FLAGS.FLARE_SHORT_SET && (
        <Route path={LEARN_ROUTES.flare} element={<Navigate to={LEARN_ROUTES.dashboard} replace />} />
      )}
      {!FEATURE_FLAGS.CCS && <Route path="/learn/*" element={<Navigate to="/" replace />} />}
      {!FEATURE_FLAGS.CCS && <Route path="/learn" element={<Navigate to="/" replace />} />}

      {/* FC UAT build (Paul's Novara-FC) — behind FEATURE_FLAGS.FC_UAT. Advisory only. */}
      {FEATURE_FLAGS.FC_UAT && (
        <Route
          path="/fc-uat"
          element={
            <ProtectedRoute>
              <FcUatPage />
            </ProtectedRoute>
          }
        />
      )}
      {!FEATURE_FLAGS.FC_UAT && <Route path="/fc-uat" element={<Navigate to="/" replace />} />}

      {/* User Routes */}
      <Route
        path="/me"
        element={
          <ProtectedRoute>
            <UserDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/me/profile"
        element={
          <ProtectedRoute>
            <EditProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/me/invites"
        element={
          <ProtectedRoute>
            <MyInvitesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/me/reputation"
        element={
          <ProtectedRoute>
            <MyReputationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/me/sjp"
        element={
          <ProtectedRoute>
            <MySJPPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/security"
        element={
          <ProtectedRoute>
            <SecuritySettingsPage />
          </ProtectedRoute>
        }
      />
      {/* In-app notification inbox (KAN-533). Reached from the header bell. */}
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        }
      />

      {/* Email-change confirmation (public — landed from the email link) */}
      <Route path="/confirm-email-change" element={<ConfirmEmailChangePage />} />
      <Route path="/guardian-approve" element={<GuardianApprovePage />} />

      {/* COMP — New Concepts Competition. Server-gated, not build-gated: every page asks
          /api/competition first and sends the visitor home on a 404, which is what the server
          answers while COMPETITION_ENABLED is off. One switch, on the server. */}
      <Route path="/competition" element={<CompetitionPage view="info" />} />
      <Route path="/competition/rules" element={<CompetitionPage view="rules" />} />
      <Route
        path="/competition/enter"
        element={
          <ProtectedRoute>
            <CompetitionEntryPage />
          </ProtectedRoute>
        }
      />

      {/* Public User Profile */}
      <Route path="/u/:id" element={<PublicUserPage />} />

      {/* Judgment Pipeline — Mind Compiler.
          Gated behind FEATURE_FLAGS.JUDGMENT_SURFACES since KAN-455. Both pages work and
          both reached nobody: nothing in the product linked to either, so they were
          reachable only by typing the URL. That is an absence of a decision rather than a
          decision, and the flag is what turns it into one. The reasoning (the Minimum
          Version names no Judgment interface; §X puts the pipeline at Day 60) lives on the
          flag, not here, so it is read by whoever considers flipping it. */}
      {FEATURE_FLAGS.JUDGMENT_SURFACES && (
        <Route
          path="/mind-compiler"
          element={
            <ProtectedRoute>
              <MindCompilerPage />
            </ProtectedRoute>
          }
        />
      )}
      {FEATURE_FLAGS.JUDGMENT_SURFACES && (
        <Route
          path="/judgment-pipeline"
          element={
            <ProtectedRoute>
              <JudgmentPipelinePage />
            </ProtectedRoute>
          }
        />
      )}
      {!FEATURE_FLAGS.JUDGMENT_SURFACES && (
        <Route path="/mind-compiler" element={<Navigate to="/" replace />} />
      )}
      {!FEATURE_FLAGS.JUDGMENT_SURFACES && (
        <Route path="/judgment-pipeline" element={<Navigate to="/" replace />} />
      )}

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute isAdmin>
            <AdminDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute isAdmin>
            <AdminUsersPage />
          </ProtectedRoute>
        }
      />
      {/* Admin → Roles: who may edit content, who may approve it. Narrow surface —
          the steward-gated user records stay on /admin/users. */}
      <Route
        path="/admin/roles"
        element={
          <ProtectedRoute isAdmin>
            <AdminRolesPage />
          </ProtectedRoute>
        }
      />
      {/* Admin → Test runs (KAN-552): record an internal test run done by hand. Open to admins
          and to accounts in TEST_RUNNER_EMAILS (Paul, Michael) without the admin role. */}
      <Route
        path="/admin/test-runs"
        element={
          <ProtectedRoute isTestRunner>
            <AdminTestRunsPage />
          </ProtectedRoute>
        }
      />
      {/* Admin → Testers (KAN-259): the tester roster for the seed-user test. Admin only:
          it lists testers' emails. */}
      <Route
        path="/admin/testers"
        element={
          <ProtectedRoute isAdmin>
            <AdminTestersPage />
          </ProtectedRoute>
        }
      />
      {/* Admin → Opportunities (KAN-259): what the learner Opportunity flow needs before its
          flag is switched on. Admin only. */}
      <Route
        path="/admin/opportunities"
        element={
          <ProtectedRoute isAdmin>
            <AdminOpportunitiesPage />
          </ProtectedRoute>
        }
      />
      {/* Admin → Competition (KAN-574): status (shown with the flag off), registrations,
          entries, CSVs and entry files. */}
      <Route
        path="/admin/competition"
        element={
          <ProtectedRoute isAdmin>
            <AdminCompetitionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/ccs-telemetry"
        element={
          <ProtectedRoute isAdmin>
            <AdminCcsTelemetryPage />
          </ProtectedRoute>
        }
      />
      {/* Admin → Content desk: pick the content type first, then its list. */}
      <Route
        path="/admin/content"
        element={
          <ProtectedRoute isEditor>
            <AdminContentHubPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/content/bouts"
        element={
          <ProtectedRoute isEditor>
            <AdminBoutBankPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/content/challenges"
        element={
          <ProtectedRoute isEditor>
            <AdminChallengeListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/content/coverage"
        element={
          <ProtectedRoute isEditor>
            <AdminCurriculumCoveragePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/content/bouts/:contentUid"
        element={
          <ProtectedRoute isEditor>
            <AdminBoutItemPage />
          </ProtectedRoute>
        }
      />
      {/* Admin → Content: the FC assessment bank editor (versioned, approve-to-serve) */}
      <Route
        path="/admin/fc-bank"
        element={
          <ProtectedRoute isEditor>
            <AdminFcBankPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/fc-bank/:itemId"
        element={
          <ProtectedRoute isEditor>
            <AdminFcItemPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/questions"
        element={
          <ProtectedRoute isAdmin>
            <AdminQuestionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/new-words"
        element={
          <ProtectedRoute isAdmin>
            <AdminNewWordsPage />
          </ProtectedRoute>
        }
      />

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" />
        </AuthProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
